import React from 'react';
import { Col, Layout, Row, Spin, Space } from 'antd';
import { Content, Header } from 'antd/es/layout/layout';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { useLoan } from '../stores/store';
import { usePrequal } from '../stores/prequalStore';
import { ClockCircleOutlined, LockOutlined } from '@ant-design/icons';

import styles from './prequal.module.scss';
import prequalProgressBarStyles from './components/PrequalProgressBar/PrequalProgressBar.module.scss';
import { generateUrlParams, getPrequalSource, loadPrequalOverrides } from '../utils';
import { BOAT_TRADER_SOURCE_ID, LendApiPrequalTab, PUBLIC_URL } from '../constants';
import { AppFooter } from './components/AppFooter/AppFooter';
import useConfig from '../stores/config';
import { useTheme } from '../hooks/useTheme';
import Logo from '../loan/components/Logo';
import PrequalProgressBar from './components/PrequalProgressBar/PrequalProgressBar';
import { TABS_THAT_SHOULD_ALWAYS_SHOW_HEADER } from './prequalProgressConfig';

type MobileVisibleIndex = 0 | 1 | 2;

type Props = {
  children: React.ReactNode;
  className?: string;
  currentTab?: LendApiPrequalTab | string;
  lastKnownTab?: LendApiPrequalTab;
  iconTexts?: string[];
  mobileVisibleIndex?: MobileVisibleIndex;
  showProgressBar?: boolean;
  headerRight?: React.ReactNode;
  centerLogo?: boolean;
  hideChrome?: boolean;
};

const DEFAULT_MOBILE_VISIBLE_INDEX: MobileVisibleIndex = 2;

const StandardHeader = ({
  iconTexts,
  mobileVisibleIndex = DEFAULT_MOBILE_VISIBLE_INDEX,
}: {
  iconTexts: string[];
  mobileVisibleIndex?: MobileVisibleIndex;
}) => (
  <Row className={styles.subheaderRow}>
    <Col
      className={classNames(styles.subheaderCol, {
        [styles.subheaderColMobileVisible]: mobileVisibleIndex === 0,
      })}
    >
      <Space size={0}>
        <ClockCircleOutlined className={styles.subheaderIcon} />
        <span>{iconTexts[0] || 'Quick 3 min process'}</span>
      </Space>
    </Col>
    <Col
      className={classNames(styles.subheaderCol, {
        [styles.subheaderColMobileVisible]: mobileVisibleIndex === 1,
      })}
    >
      <Space size={0}>
        <img
          src={`${PUBLIC_URL}/static/trident/shield-icon.svg`}
          className={styles.subheaderIcon}
          alt=""
        />
        <span>{iconTexts[1] || 'No impact to your credit score'}</span>
      </Space>
    </Col>
    <Col
      className={classNames(styles.subheaderCol, {
        [styles.subheaderColMobileVisible]: mobileVisibleIndex === 2,
      })}
    >
      <Space size={0}>
        <LockOutlined className={styles.subheaderIcon} />
        <span>{iconTexts[2] || 'Secure Encryption'}</span>
      </Space>
    </Col>
  </Row>
);

export default observer(function PrequalLayout({
  children,
  className,
  iconTexts = [],
  mobileVisibleIndex,
  showProgressBar = false,
  currentTab,
  lastKnownTab,
  headerRight,
  centerLogo = false,
  hideChrome = false,
}: Props) {
  const loanStore = useLoan();
  const config = useConfig();
  const prequalStore = usePrequal();
  const { prequal } = prequalStore;
  const urlQueryParams = generateUrlParams(null);
  const theme = useTheme(loanStore, config, true);
  const tabThatShouldAlwaysShowHeader = currentTab
    ? TABS_THAT_SHOULD_ALWAYS_SHOW_HEADER.has(currentTab as LendApiPrequalTab)
    : true;
  const hasRequirementsForProgressBar = showProgressBar && currentTab;
  const isShowingProgressBar = !tabThatShouldAlwaysShowHeader && hasRequirementsForProgressBar;

  if (theme == null) {
    return (
      <Layout className={styles.mainLayout}>
        <Spin size="large" style={{ marginTop: '2rem', display: 'block' }} />
      </Layout>
    );
  }

  const sourceId = getPrequalSource(config, prequal);
  loadPrequalOverrides(theme, sourceId);

  return (
    <Layout className={styles.mainLayout}>
      {!hideChrome && (
        <Header
          className={classNames(styles.header, {
            [styles.headerBoatTrader]: sourceId === BOAT_TRADER_SOURCE_ID,
          })}
        >
          <Row
            className={styles.headerRow}
            justify={centerLogo ? 'center' : 'space-between'}
            align="middle"
          >
            <Col>
              <Logo
                imgSrc={theme.logo}
                linkUrl={theme.marketingWebsiteUrl}
                urlQueryParams={urlQueryParams}
              />
            </Col>
            {!centerLogo && headerRight && (
              <Col className={styles.headerRightCol}>{headerRight}</Col>
            )}
          </Row>
        </Header>
      )}
      {!hideChrome && (
        <Header
          className={classNames(styles.subheader, {
            [prequalProgressBarStyles.prequalSubheaderProgressbar]: isShowingProgressBar,
          })}
        >
          {isShowingProgressBar ? (
            <PrequalProgressBar currentTab={currentTab} lastKnownTab={lastKnownTab} />
          ) : (
            <StandardHeader iconTexts={iconTexts} mobileVisibleIndex={mobileVisibleIndex} />
          )}
        </Header>
      )}
      <Content
        className={classNames(styles.content, className, {
          [styles.contentHideChrome]: hideChrome,
        })}
        data-testid="prequal-funnel-content"
      >
        <div className="form-container">{children}</div>
      </Content>
      {!hideChrome && <AppFooter />}
    </Layout>
  );
});
